import { useCallback, useEffect, useRef, useState } from "react";
import { useSignalR } from "../Contexts/SignalRContext";
import { DEFAULT_HYDROVAULT_CONFIG } from "../Helpers/hydrovaultConfig";
import apiClient from "../Helpers/apiClient";

export const WORKFLOW_STAGES = { CALCULATE_PRICE: 0, GENERATE_MODEL: 1, DOWNLOAD_AND_QUOTE: 2 };
// SVF companion assets resolve relative to this directory.
export const HYDROVAULT_STANDARD_MODEL_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}/HydroVaultStdModel/output/1/result.svf`;
const initialState = () => ({ activeStep: 0, modelConfig: { ...DEFAULT_HYDROVAULT_CONFIG }, quantity: 1,
    city: "", priceBreakdown: null, isGenerating: false, workItemId: "", viewerPath: HYDROVAULT_STANDARD_MODEL_URL, downloadUrls: {} });
function restore(key) {
    try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        if (!saved || Date.now() - saved.lastSaved >= 86400000) return initialState();
        const restored = { ...initialState(), ...saved };
        // Older saved configurations had an empty initial preview URL.
        if (!restored.viewerPath && restored.activeStep !== WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE) {
            restored.viewerPath = HYDROVAULT_STANDARD_MODEL_URL;
        }
        return restored;
    } catch { return initialState(); }
}

export function useHydrovaultState(quoteId, showNotification) {
    const storageKey = `hydrovaultState:${quoteId || "draft"}`;
    const [workflow, setWorkflow] = useState(() => restore(storageKey));
    const current = useRef(workflow);
    current.current = workflow;
    const { isConnected, connectionId, registerGlobalHandler, unregisterGlobalHandler, getPendingMessages } = useSignalR();
    const update = useCallback((patch) => {
        const next = { ...current.current, ...(typeof patch === "function" ? patch(current.current) : patch) };
        current.current = next;
        // Persist immediately, including a submission response that arrives after navigation.
        sessionStorage.setItem(storageKey, JSON.stringify({ ...next, lastSaved: Date.now() }));
        setWorkflow(next);
    }, [storageKey]);

    const refreshResult = useCallback(async (workItemId) => {
        try {
            const { data } = await apiClient.get(`/api/hydrovault/workitems/${workItemId}`);
            if (current.current.workItemId !== workItemId) return;
            if (data.status === "failed") {
                update({ isGenerating: false });
                showNotification("Model generation failed. Please try again.", "error");
            } else if (data.status === "completed") {
                const wasGenerating = current.current.isGenerating;
                update({ viewerPath: data.viewerPath ? `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}/${data.viewerPath.replace(/^\/+/, "")}` : "",
                    isGenerating: false, activeStep: WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE, downloadUrls: data.downloadUrls });
                if (wasGenerating) showNotification("Hydrovault model generated successfully.");
            }
        } catch {
            // SignalR and the next status refresh can recover transient connection failures.
        }
    }, [update, showNotification]);

    const onComponents = useCallback((path) => {
        const workItemId = typeof path === "string" && path.match(/Workitems\/(hydrovault_[a-f0-9]+)\//i)?.[1];
        if (!workItemId || workItemId !== current.current.workItemId) return;
        refreshResult(workItemId);
    }, [refreshResult]);

    useEffect(() => {
        if (!workflow.workItemId) return;
        // Recheck restored completed jobs so a previously saved, invalid SVF URL is replaced.
        refreshResult(workflow.workItemId);
        if (!workflow.isGenerating) return;
        const interval = setInterval(() => refreshResult(workflow.workItemId), 10000);
        return () => clearInterval(interval);
    }, [workflow.workItemId, workflow.isGenerating, refreshResult]);

    const onComplete = useCallback((message) => {
        if (current.current.workItemId && message?.includes(`Workitems/${current.current.workItemId}/`) && message.includes("Error")) {
            update({ isGenerating: false });
            showNotification("Model generation failed. Please try again.", "error");
        }
    }, [update, showNotification]);

    useEffect(() => {
        if (!isConnected) return;
        registerGlobalHandler("onComponents", "hydrovault", onComponents);
        registerGlobalHandler("onComplete", "hydrovault", onComplete);
        getPendingMessages("onComponents", "hydrovault").forEach(({ data }) => onComponents(data));
        getPendingMessages("onComplete", "hydrovault").forEach(({ data }) => onComplete(data));
        return () => {
            unregisterGlobalHandler("onComponents", "hydrovault");
            unregisterGlobalHandler("onComplete", "hydrovault");
        };
    }, [isConnected, registerGlobalHandler, unregisterGlobalHandler, getPendingMessages, onComponents, onComplete]);

    const resetWorkflow = useCallback(() => update(initialState()), [update]);
    return { ...workflow, update, resetWorkflow, isSignalRConnected: isConnected, connectionId };
}
